import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_STRING } from '@modeso/aumico-lib-core-fe';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { ISelectOptions } from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';
import { startWith } from 'rxjs';
@UntilDestroy()
@Component({
    selector: 'select-extra-settings',
    templateUrl: './select-extra-settings.component.html',
    styleUrls: ['./select-extra-settings.component.scss'],
})
export class SelectExtraSettingsComponent implements OnInit, OnChanges {
    optionsForm: FormGroup;
    translatedOptions: IDynamicKeyValue[];
    @Input() showOptions: boolean = false;
    @Input() componentOptions: ISelectOptions;
    @Input() activeLanguage: string;
    @Input() languages: string[];
    @Input() selectText: string | undefined;

    @Output() optionsChanged = new EventEmitter<ISelectOptions>();
    @Output() formStatusChanged = new EventEmitter<boolean>();
    @Output() closeOptions = new EventEmitter<boolean>();
    initialOptions: ISelectOptions = undefined;
    constructor(
        private readonly fb: FormBuilder,
        private readonly matIconRegister: MatIconRegistry,
        private readonly domSanitizer: DomSanitizer,
    ) {
        this.addSvgIcons();
    }
    addSvgIcons() {
        this.matIconRegister.addSvgIcon(
            'aumico-pattern-icon-remove',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-icon-remove.svg'),
        );
    }
    ngOnInit(): void {
        this.initForm();
        this.listenOnFormChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectText'] && this.optionsForm != null) {
            this.optionsForm.get('text').setValue(this.selectText);
        }
    }

    get options() {
        return this.optionsForm.get('options') as FormArray;
    }

    get text() {
        return this.optionsForm.get('text') as FormArray;
    }

    addOption() {
        this.options.push(this.fb.control(EMPTY_STRING, Validators.required));
        this.translatedOptions.push(this.createTranslationObject());
    }

    onRemoveOption(index: number): void {
        this.options.removeAt(index);
        this.translatedOptions.splice(index, 1);
    }

    private createTranslationObject(): IDynamicKeyValue {
        const object: IDynamicKeyValue = {};
        this.languages.forEach((language) => {
            object[language] = EMPTY_STRING;
        });

        return object;
    }

    private initForm(): void {
        let text: string = EMPTY_STRING;
        let selectionOption: IDynamicKeyValue[] = [];
        if (this.componentOptions != null) {
            text = this.componentOptions.text[this.activeLanguage];
            selectionOption = this.componentOptions.selectionOption;
        }
        const optionsInActiveLanguage = selectionOption.map((value) => this.fb.control(value[this.activeLanguage]));

        this.optionsForm = this.fb.group({
            options: this.fb.array([...optionsInActiveLanguage], [Validators.required, Validators.minLength(2)]),
            text: this.fb.control(text, Validators.required),
        });

        this.translatedOptions = [...selectionOption];
        this.initialOptions = cloneDeep(this.getSelectedOptionsFromForm());
    }

    private getSelectedOptionsFromForm() {
        const { options, text } = this.optionsForm.getRawValue();

        options.forEach((option, index) => {
            this.translatedOptions[index][this.activeLanguage] = option;
        });

        const textTranslationText = this.createTranslationObject();
        textTranslationText[this.activeLanguage] = text;

        const selectOptions: ISelectOptions = {
            selectionOption: this.translatedOptions,
            text: textTranslationText,
        };

        return selectOptions;
    }

    private listenOnFormChanges(): void {
        this.optionsForm.statusChanges
            .pipe(startWith(this.optionsForm.status), untilDestroyed(this))
            .subscribe((status) => {
                const isFormValid = status === 'VALID';
                this.formStatusChanged.emit(isFormValid);
            });
    }

    toggleOptions() {
        this.showOptions = !this.showOptions;
    }

    onCloseOptions() {
        const selectOptions: ISelectOptions = this.getSelectedOptionsFromForm();
        this.optionsChanged.emit(selectOptions);

        this.showOptions = !this.showOptions;
        this.closeOptions.emit(this.showOptions);
    }

    onCancelOptions() {
        const selectOptions: ISelectOptions = this.initialOptions;
        this.componentOptions = selectOptions;
        this.optionsChanged.emit(selectOptions);

        this.showOptions = !this.showOptions;
        this.closeOptions.emit(this.showOptions);
    }
}
